import { FC, MouseEvent, useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from 'reactstrap';
import { connect, useDispatch } from 'react-redux';

import {
    Link,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { useTranslation, UseTranslationResponse } from 'react-i18next';
import classNames from 'classnames';

import {
    faFont,
    faPlus,
    faMoon,
    faSignLanguage,
    faChevronLeft,
    faUniversalAccess,
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { acessibilidadeData } from '../../store/ducks/pageConfig';
import '../../../css/components/accessibility/AcessibilidadeBar.scss';
import { useAccessibilityContext } from './contexts/AccessibilityContext';
import { useZoomContext } from '../../shared/contexts/ZoomContext';
import {
    LangType,
    useAppContext,
} from '../../shared/contexts/AppContextProvider';

interface AccessibilityBarProps {
    acessibilidadeRx: { negrito: boolean; cor: string; zoom: number };
    userSessionRx: any;
    userLocalRx: any;
    instituicaoRx: { sigla_instituicao: string, codEmpresa: string, };
    fromAccessibilityPages?: boolean;
    showDarkMode?: boolean;
    guiasPesquisaRx?: any;
}

const AccessibilityBar: FC<AccessibilityBarProps> = ({
    acessibilidadeRx,
    userSessionRx,
    userLocalRx,
    instituicaoRx: { sigla_instituicao,codEmpresa },
    fromAccessibilityPages,
    showDarkMode = true,
    guiasPesquisaRx
}) => {
    const isAuthenticated: boolean = userSessionRx || userLocalRx;
    const dispatch = useDispatch();
    const { t }: UseTranslationResponse<'translation', undefined> =
        useTranslation();

    const { faqElementRef, searchInputRef, hasFaqs, hasSearch } =
        useAccessibilityContext();
    const { incrementZoom, resetZoom, zoom } = useZoomContext();
    const { lang, changeLang, onGoBackMobile } = useAppContext();
    const location = useLocation();
    const { pathname } = location;
    const navigate = useNavigate();
    const [optionsIsOpen, setOptionsIsOpen] = useState(false);
    const [showGoToFirstSearch, setShowGoToFirstSearch] = useState<boolean>(false);

    useEffect(() => {
        if(location.pathname.startsWith('/pesquisa_acessibilidade')){
            setShowGoToFirstSearch(true)
        }else{
            setShowGoToFirstSearch(false)
        }
    }, [location])

    const firstTabObject : {codigo: string, titulo: string}|null = guiasPesquisaRx && guiasPesquisaRx?.length > 0 ? guiasPesquisaRx[0] : null;


    const handleIncrementZoom = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        incrementZoom();
    };

    const handleResetZoom = (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        resetZoom();
    };

    const handleClickChangeContrast = (
        event: MouseEvent<HTMLAnchorElement>,
    ): void => {
        event.preventDefault();
        const { cor } = acessibilidadeRx;
        dispatch(
            acessibilidadeData({
                ...acessibilidadeRx,
                cor: cor === 'contraste' ? '' : 'contraste',
            }),
        );
    };

    const handleClickDuvidasFrequentes = (
        event: MouseEvent<HTMLAnchorElement>,
    ) => {
        event.preventDefault();

        if (pathname.startsWith('/pesquisa_acessibilidade')) {
            navigate('/pesquisa_acessibilidade/duvidas-frequentes');
        }

        faqElementRef?.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleGoBack = (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        if(onGoBackMobile && typeof onGoBackMobile === 'function'){
            onGoBackMobile();
        }else{
            const lastSearchUrl = localStorage.getItem('lastAccessibilitySearchUrl');

            if(lastSearchUrl){
                navigate(lastSearchUrl)
                return
            }  
            navigate(-1)
        }
    };

    const handleFocusSearch = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        searchInputRef?.current?.focus();
    };

    const handleClickShowOptions = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setOptionsIsOpen(true)
    }


    return (
        <>
            {/* MOBILE */}
            <nav
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1040,
                }}
                className="AcessibilidadeBar bg bg-degrade-mobile">
                <div className="container-fluid py-2 d-block d-md-none bkdark50">
                    <div className="row">
                        <div className="col-2">
                            <a
                                onClick={handleGoBack}
                                href={`#${t('Voltar').toLowerCase()}`}
                                accessKey="0"
                                title={t('Voltar')}
                                className="branco"
                            >
                                <FontAwesomeIcon
                                    icon={faChevronLeft as IconProp}
                                />
                            </a>
                        </div>
                        <div className="col-10 f700 branco">
                            {/*headerTitle !== null ? headerTitle : (sigla_instituicao ?? t('Acessibilidade'))*/}
                        </div>
                    </div>
                </div>
            </nav>
            {/* MOBILE */}

            {/* DESKTOP */}
            <nav
                className="AcessibilidadeBar bg b-solid-bottom b-w2 b-branco70 py-1 justify-content-center d-md-flex d-none">
                <ul
                    className="d-flex align-items-center">
                    {
                        !fromAccessibilityPages || optionsIsOpen ? (
                            <>
                                {pathname !== '/pesquisa_acessibilidade' ? (
                                    <li>
                                        <Link to="/pesquisa_acessibilidade" accessKey="1">
                                            {t('AccessibilityBar.IrParaBuscaAcessivel')}
                                        </Link>
                                    </li>
                                ) : (
                                    ''
                                )}
                                {hasSearch ? (
                                    <li>
                                        <a
                                            onClick={handleFocusSearch}
                                            href={`#${t(
                                                'AccessibilityBar.IrParaABusca',
                                            ).toLowerCase()}`}
                                            accessKey="1"
                                        >
                                            {t('AccessibilityBar.IrParaABusca')}
                                        </a>
                                    </li>
                                ) : (
                                    ''
                                )}

                                {
                                    firstTabObject && showGoToFirstSearch ? (
                                        <li>
                                            <Link to={`/${firstTabObject?.codigo}`} accessKey="2">
                                                {t(`AccessibilityBar.IrParaPrimeiraPesquisa`, {
                                                    firstSearch: firstTabObject?.titulo
                                                })}
                                            </Link>
                                        </li>
                                        ): ''
                                }

                                {!isAuthenticated && pathname !== '/login' ? (
                                    <li>
                                        <Link to="/login" accessKey="2">
                                            {t('AccessibilityBar.LogarNoSistema')}
                                        </Link>
                                    </li>
                                ) : (
                                    ''
                                )}

                                {hasFaqs ||
                                pathname.startsWith('/pesquisa_acessibilidade') ? (
                                    <li>
                                        <a
                                            onClick={handleClickDuvidasFrequentes}
                                            href="pesquisa_acessibilidade_pergunta"
                                            accessKey="3"
                                        >
                                            {t('DuvidasFrequentes')}
                                        </a>
                                    </li>
                                ) : (
                                    ''
                                )}

                                {/* <li className='so_para_leitores_tela'>
                            <a href="/sitemap" accesskey="4" title="Ir ao mapa do site">
                                <i class="fa fa-sitemap"></i> Mapa do site
                            </a>
                        </li> */}
                                {pathname !== '/pesquisa_acessibilidade/sobre' ? (
                                    <li>
                                        <Link
                                            to="/pesquisa_acessibilidade/sobre"
                                            accessKey="4"
                                            title={t(
                                                'AccessibilityBar.SobreAAcessibilidade',
                                            )}
                                            className="mr-1"
                                            rel="nofollow"
                                        >
                                            <FontAwesomeIcon
                                                icon={faUniversalAccess as IconProp}
                                                className="mr-1"
                                            />
                                            {t('Acessibilidade')}
                                        </Link>
                                    </li>
                                ) : (
                                    ''
                                )}

                                {
                                    showDarkMode ? (
                                        <li className="opt-icon opt-contrast position-relative">
                                            <a
                                                className={classNames({
                                                    'activated-accessibility-link':
                                                        acessibilidadeRx.cor === 'contraste',
                                                })}
                                                href="#"
                                                accessKey="5"
                                                title={t('AccessibilityBar.MudarOContraste')}
                                                onClick={handleClickChangeContrast}
                                            >
                                                <FontAwesomeIcon icon={faMoon as IconProp} />
                                                <span
                                                    aria-hidden="true"
                                                    className="so_para_leitores_tela"
                                                >
                                {t('AccessibilityBar.Contraste')}
                            </span>
                                            </a>
                                        </li>
                                    ) : ''
                                }

                                <li className="opt-icon opt-increase-text">
                                    <a
                                        className={classNames({
                                            'activated-accessibility-link': zoom > 100,
                                        })}
                                        onClick={handleIncrementZoom}
                                        href="#"
                                        accessKey="6"
                                        title={t('AccessibilityBar.AumentarTamanhoTexto')}
                                        aria-label={t('AccessibilityBar.AumentarTamanhoTexto')}
                                    >
                                        <FontAwesomeIcon icon={faFont as IconProp} />
                                        <FontAwesomeIcon icon={faPlus as IconProp} />
                                        <span
                                            aria-hidden="true"
                                            className="so_para_leitores_tela"
                                        >
                                {t('AccessibilityBar.AumentarOTexto')}
                            </span>
                                    </a>
                                </li>
                                <li className="opt-icon opt-normal-text">
                                    <a
                                        className={classNames({
                                            'activated-accessibility-link': zoom === 100,
                                        })}
                                        onClick={handleResetZoom}
                                        href="#"
                                        accessKey="7"
                                        title={t(
                                            'AccessibilityBar.RetornarTamanhoPadraoTexto',
                                        )}
                                        aria-label={t(
                                            'AccessibilityBar.RetornarTamanhoPadraoTexto',
                                        )}
                                    >
                                        <FontAwesomeIcon icon={faFont as IconProp} />
                                        <span
                                            aria-hidden="true"
                                            className="so_para_leitores_tela"
                                        >
                                {t('AccessibilityBar.RetornarTamanhoPadrao')}
                            </span>
                                    </a>
                                </li>
                                <li className="opt-icon opt-libras">
                                    <a
                                        href="http://www.vlibras.gov.br"
                                        target="_blank"
                                        accessKey="8"
                                        title={t('AccessibilityBar.IrParaOSiteVlibras')}
                                        className="0"
                                        rel="nofollow"
                                    >
                                        <FontAwesomeIcon
                                            icon={faSignLanguage as IconProp}
                                        />
                                        <span
                                            aria-hidden="true"
                                            className="so_para_leitores_tela"
                                        >
                                {t('AccessibilityBar.AcessivelEmLibras')}
                            </span>
                                    </a>
                                </li>
                                <li className="pl-5">
                                    {/*  @ts-ignore */}
                                    <Input
                                        id="select-language-top-accessibility"
                                        type="select"
                                        value={lang}
                                        className="bkcinza3 branco b-none rad10 s12"
                                        onChange={e => {
                                            changeLang(e.target.value as LangType);
                                        }}
                                    >
                                        <option value="pt" label="Idioma Português" />
                                        <option value="en" label="English Language" />
                                        <option value="es" label="Lengua Española" />
                                    </Input>
                                </li>
                            </>
                        ) : (
                            <li>
                                <a
                                    onClick={handleClickShowOptions}
                                    href={`#${t(
                                        'AccessibilityBar.MostrarOpcoesDeAcessibilidade',
                                    ).toLowerCase()}`}
                                    accessKey="1"
                                >
                                    {t('AccessibilityBar.MostrarOpcoesDeAcessibilidade')}
                                </a>
                            </li>
                        )

                    }
                </ul>
            </nav>
            {/* DESKTOP */}
        </>
    );
};

const mapStateToProps = (state: any) => ({
    instituicaoRx: state.instituicao.data,
    acessibilidadeRx: state.pageConfig.acessibilidade,
    userSessionRx: state.auth.user,
    userLocalRx: state.authPersist.user,
    guiasPesquisaRx: state.pageConfig.guiasPesquisa,
});

export default connect(mapStateToProps)(AccessibilityBar);
